import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { ArrowLeft, Save, Copy, Mail, Check, X, History, Phone, PhoneOff, Clock, Zap, Trophy, Send } from "lucide-react";

const STATUSES = ["Nuovo", "Da verificare", "Valido", "Duplicato", "Non valido", "Archiviato"];
const NONE = "__none__";

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState({});
  const [status, setStatus] = useState("Nuovo");
  const [courseId, setCourseId] = useState(NONE);

  const { data: lead, isLoading } = useQuery({
    queryKey: ["lead", id],
    queryFn: async () => (await api.get(`/leads/${id}`)).data,
  });
  const { data: courses } = useQuery({
    queryKey: ["courses"],
    queryFn: async () => (await api.get("/courses")).data,
  });
  const { data: history } = useQuery({
    queryKey: ["lead-history", id],
    queryFn: async () => (await api.get(`/leads/${id}/history`)).data,
  });
  const { data: callLog } = useQuery({
    queryKey: ["lead-calls", id],
    queryFn: async () => (await api.get(`/leads/${id}/calls`)).data,
  });
  const { data: emailLog } = useQuery({
    queryKey: ["lead-emails", id],
    queryFn: async () => (await api.get(`/leads/${id}/emails`)).data,
  });
  const { data: purchaseLog } = useQuery({
    queryKey: ["lead-purchases", id],
    queryFn: async () => (await api.get(`/leads/${id}/purchases`)).data,
  });
  const [openEmail, setOpenEmail] = useState(null);

  useEffect(() => {
    if (lead) {
      setForm({ ...lead.normalized });
      setStatus(lead.status);
      setCourseId(lead.course_id || NONE);
    }
  }, [lead]);

  const save = useMutation({
    mutationFn: async () =>
      (await api.put(`/leads/${id}`, {
        normalized: form,
        status,
        course_id: courseId === NONE ? null : courseId,
      })).data,
    onSuccess: () => {
      toast.success("Lead aggiornato");
      qc.invalidateQueries({ queryKey: ["lead", id] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e) => {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" && d ? d : "Errore durante il salvataggio");
    },
  });

  const merge = useMutation({
    mutationFn: async (dupIds) =>
      (await api.post("/leads/merge", { primary_id: id, duplicate_ids: dupIds })).data,
    onSuccess: () => {
      toast.success("Duplicati uniti");
      qc.invalidateQueries({ queryKey: ["lead", id] });
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: () => toast.error("Errore durante l'unione"),
  });

  if (isLoading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-40" /><Skeleton className="h-96 w-full" /></div>;
  }

  const field = (k) => form[k] ?? "";
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const ConsentBadge = ({ value }) =>
    value === true ? (
      <span className="inline-flex items-center gap-1 text-sm text-emerald-700"><Check className="h-4 w-4" /> Sì</span>
    ) : value === false ? (
      <span className="inline-flex items-center gap-1 text-sm text-rose-600"><X className="h-4 w-4" /> No</span>
    ) : (
      <span className="text-sm text-muted-foreground">Non specificato</span>
    );

  const fmtDay = (iso) => (iso
    ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric", timeZone: "Europe/Rome" })
    : "—");

  const fmtDateTime = (iso) => (iso
    ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" })
    : "—");

  const fmtDuration = (s) => {
    s = Number(s) || 0;
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  const wonDeals = lead.pipedrive?.won_deals || [];
  const wonAt = wonDeals.map((d) => d.won_at).filter(Boolean).sort()[0] || null;
  const daysToWin = (wonAt && lead.received_at)
    ? (new Date(wonAt) - new Date(lead.received_at)) / 86400000 : null;
  const daysSince = (!wonAt && lead.received_at)
    ? (Date.now() - new Date(lead.received_at)) / 86400000 : null;
  const fmtDaysNum = (d) => (d == null ? "—" : d < 1 ? `${Math.round(d * 24)} ore` : `${Math.round(d)} giorni`);

  const fn = lead.pipedrive?.funnel || {};
  const forwardedAt = fn.assigned ? fn.assigned_at : null;
  const forwardIsInoltro = fn.assigned_source === "inoltro";
  // Lead "già presente": il commerciale aveva già il contatto PRIMA di questo lead.
  // Non interessa la data storica: conta se DOPO l'ultima richiesta è stato ripreso in
  // carico (prima chiamata o email inviata dopo l'arrivo del lead).
  const alreadyOwned = fn.assigned && fn.owner_already;
  const recvMs = lead.received_at ? new Date(lead.received_at).getTime() : null;
  const firstCallAfter = (callLog?.calls || [])
    .filter((c) => !c.inbound && recvMs != null && new Date(c.start_time).getTime() >= recvMs)
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))[0];
  const firstEmailAfter = (emailLog?.emails || [])
    .filter((m) => !m.inbound && recvMs != null && m.internal_ms >= recvMs)
    .sort((a, b) => a.internal_ms - b.internal_ms)[0];
  const takenAfterMs = [
    firstCallAfter ? new Date(firstCallAfter.start_time).getTime() : null,
    firstEmailAfter ? firstEmailAfter.internal_ms : null,
  ].filter((x) => x != null).sort((a, b) => a - b)[0] ?? null;
  const forwardLabel = !fn.assigned
    ? "Inoltro al commerciale"
    : alreadyOwned ? "Ripreso in carico il"
    : forwardIsInoltro ? "Inoltrata al commerciale il" : "Presa in carico il";
  const forwardValue = !fn.assigned
    ? "Non ancora inoltrata"
    : alreadyOwned
      ? (takenAfterMs != null ? fmtDateTime(new Date(takenAfterMs).toISOString()) : "Non ancora ripreso")
      : fmtDateTime(forwardedAt);
  const forwardTone = alreadyOwned
    ? (takenAfterMs != null ? "sky" : "amber")
    : (fn.assigned ? "sky" : "amber");
  const forwardSub = !fn.assigned
    ? "in attesa di assegnazione"
    : alreadyOwned
      ? `${takenAfterMs != null ? "" : "dopo la richiesta · "}${fn.commercial || "—"}`
      : `${forwardIsInoltro ? "da dem@italiacorsi.it → " : ""}${fn.commercial || "—"}`;

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      <div className="flex items-center justify-between">
        <Button variant="ghost" data-testid="back-button" onClick={() => navigate("/leads")} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Torna ai lead
        </Button>
        <Button data-testid="save-lead-button" onClick={() => save.mutate()} disabled={save.isPending} className="gap-2">
          <Save className="h-4 w-4" /> Salva modifiche
        </Button>
      </div>

      <Card className="shadow-sm" data-testid="lead-summary-card">
        <CardContent className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryStat icon={History} tone="slate" label="Lead arrivato il" value={fmtDateTime(lead.received_at)} />
          <SummaryStat icon={Send} tone={forwardTone} label={forwardLabel} value={forwardValue} sub={forwardSub} />
          <SummaryStat icon={Trophy} tone={wonAt ? "emerald" : "amber"} label="Concluso il" value={wonAt ? fmtDateTime(wonAt) : "Non ancora concluso"} />
          <SummaryStat icon={Clock} tone={wonAt ? "emerald" : "slate"} label={wonAt ? (daysToWin != null && daysToWin < 1 ? "Tempo per chiudere" : "Giorni per chiudere") : "In attesa da"} value={wonAt ? fmtDaysNum(daysToWin) : fmtDaysNum(daysSince)} />
        </CardContent>
      </Card>

      {lead.auto_forward?.sent && (
        <Card className="border-2 border-emerald-300 bg-emerald-50 shadow-sm" data-testid="lead-auto-forward-card">
          <CardContent className="flex flex-wrap items-center gap-2 p-4 text-sm text-emerald-800">
            <Send className="h-4 w-4" />
            <span>
              Girato in <b>automatico dal sistema</b> a <b>{lead.auto_forward.to || "—"}</b>
              {lead.auto_forward.mailbox ? ` (${lead.auto_forward.mailbox})` : ""}
              {lead.auto_forward.at ? ` il ${fmtDateTime(lead.auto_forward.at)}` : ""}
            </span>
          </CardContent>
        </Card>
      )}

      {lead.pipedrive?.won_deals?.length > 0 && (
        <Card className="border-emerald-300 bg-emerald-50 shadow-sm" data-testid="lead-won-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 font-semibold text-emerald-800">
              <Trophy className="h-5 w-5" /> Lead aggiudicato ({lead.pipedrive.won_deals.length})
            </div>
            <div className="mt-3 space-y-2">
              {lead.pipedrive.won_deals.map((d, i) => (
                <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-emerald-200 bg-white px-3 py-2" data-testid="won-deal-row">
                  <span className="text-sm font-medium">{d.title || "Affare vinto"}</span>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="font-semibold text-emerald-700">{`€ ${Math.round(Number(d.value) || 0).toLocaleString("it-IT")}`}</span>
                    <span className="text-muted-foreground">{fmtDay(d.won_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {purchaseLog?.count > 0 && (
        <Card className="border-emerald-200 shadow-sm" data-testid="lead-purchases-card">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2 font-display text-base tracking-tight">
              <Trophy className="h-4 w-4 text-emerald-600" /> Storico acquisti ({purchaseLog.count})
              <span className="ml-auto rounded-full bg-emerald-100 px-2.5 py-0.5 text-sm font-bold text-emerald-700">
                Totale € {Math.round(purchaseLog.total_value).toLocaleString("it-IT")}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="purchase-list">
              {purchaseLog.purchases.map((p, i) => (
                <div
                  key={i}
                  data-testid="purchase-row"
                  className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 ${p.is_current ? "bg-emerald-50/60" : ""}`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium" title={p.title}>{p.title || "Affare vinto"}</p>
                    <p className="text-xs text-muted-foreground">
                      {p.product}{p.commercial ? ` · ${p.commercial}` : ""}
                      {p.is_current ? " · questa richiesta" : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="font-semibold text-emerald-700">€ {Math.round(p.value).toLocaleString("it-IT")}</span>
                    <span className="whitespace-nowrap text-muted-foreground">{fmtDay(p.won_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {lead.is_duplicate && (
        <Card className="border-orange-200 bg-orange-50 shadow-sm">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-sm text-orange-800">
              <Copy className="h-4 w-4" />
              Potenziale duplicato: condivide email o telefono con {lead.duplicate_of?.length} altro/i lead.
            </div>
            <Button
              size="sm"
              variant="outline"
              data-testid="merge-button"
              className="border-orange-300 bg-white text-orange-800 hover:bg-orange-100"
              onClick={() => merge.mutate(lead.duplicate_of || [])}
              disabled={merge.isPending}
            >
              Unisci duplicati
            </Button>
          </CardContent>
        </Card>
      )}

      {history && history.count > 1 && (
        <Card className="shadow-sm" data-testid="lead-history">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
              <History className="h-4 w-4" /> Storico richieste di questa persona
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(history.previously_same_course || history.previously_other_course) && (
              <div className="mb-4 flex flex-wrap gap-2">
                {history.previously_same_course && (
                  <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                    Ha già richiesto lo STESSO corso ({history.same_count}×)
                  </span>
                )}
                {history.previously_other_course && (
                  <span className="rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-700">
                    Ha richiesto ALTRI corsi ({history.other_count}×)
                  </span>
                )}
              </div>
            )}
            <ol className="relative border-l-2 border-border pl-5">
              {history.history.map((h) => (
                <li key={h.id} className="mb-5 last:mb-0" data-testid="history-item">
                  <span className={`absolute -left-[7px] mt-1 h-3 w-3 rounded-full ${h.is_current ? "bg-primary ring-4 ring-primary/20" : "bg-slate-300"}`} />
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold">{fmtDay(h.received_at)}</span>
                    {h.is_current && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">Richiesta corrente</span>}
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{h.channel || "Altro"}</span>
                  </div>
                  <p className="mt-1 text-sm">
                    Corso: <span className="font-medium">{h.course_name || "Non riconosciuto"}</span>
                    <span className="text-muted-foreground"> · {h.campaign || "—"} · {h.status}</span>
                  </p>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}

      <ContactTimeline received={lead.received_at} history={history?.history} calls={callLog?.calls} emails={emailLog?.emails} />

      {lead.calls && (
        <Card className={`shadow-sm ${lead.calls.called ? "border-emerald-200" : "border-rose-200"}`} data-testid="lead-calls-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
              {lead.calls.called ? <Phone className="h-4 w-4 text-emerald-600" /> : <PhoneOff className="h-4 w-4 text-rose-600" />}
              Chiamate (SalesTrail)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lead.calls.no_call_system && !lead.calls.called ? (
              <p className="text-sm text-slate-600">Il commerciale assegnato non usa il sistema di chiamate SalesTrail, quindi le chiamate non vengono registrate.</p>
            ) : lead.calls.called ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Stat2 label="Chiamate totali" value={lead.calls.total} />
                <Stat2 label="Risposte" value={lead.calls.answered} />
                <Stat2 label="Ultima chiamata" value={fmtDay(lead.calls.last_call_at)} />
                <Stat2 label="Da" value={lead.calls.last_agent || "—"} />
              </div>
            ) : (
              <p className="text-sm text-rose-700">Questo contatto non risulta ancora chiamato in SalesTrail.</p>
            )}

            {lead.calls.called && lead.calls.matched_secondary && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="lead-calls-secondary-note">
                <Phone className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Il numero della richiesta non risulta chiamato, ma è stato chiamato un{" "}
                  <b>numero secondario presente su Pipedrive</b>
                  {lead.calls.secondary_numbers?.length ? <>: <b>{lead.calls.secondary_numbers.join(", ")}</b></> : ""}.
                </span>
              </div>
            )}

            {callLog?.calls?.length > 0 && (
              <div className="mt-5" data-testid="lead-call-history">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Tutte le chiamate ({callLog.count})
                </p>
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-secondary/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="px-3 py-2 font-semibold">Data e ora</th>
                        <th className="px-3 py-2 font-semibold">Numero</th>
                        <th className="px-3 py-2 font-semibold">Direzione</th>
                        <th className="px-3 py-2 font-semibold">Risposta</th>
                        <th className="px-3 py-2 font-semibold">Durata</th>
                        <th className="px-3 py-2 font-semibold">Commerciale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {callLog.calls.map((c, i) => (
                        <tr key={i} className="border-t border-border" data-testid="call-row">
                          <td className="px-3 py-2 font-medium">
                            {fmtDateTime(c.start_time)}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {c.number || "—"}
                            {c.secondary && <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" title="Numero secondario presente su Pipedrive">Pipedrive</span>}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{c.inbound ? "In entrata" : "In uscita"}</td>
                          <td className="px-3 py-2">
                            {c.answered ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="h-3.5 w-3.5" /> Risposto</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-rose-600"><X className="h-3.5 w-3.5" /> Nessuna risposta</span>
                            )}
                          </td>
                          <td className="px-3 py-2 tabular-nums">{c.answered ? fmtDuration(c.duration) : "—"}</td>
                          <td className="px-3 py-2 text-muted-foreground">{c.agent || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {emailLog?.configured && emailLog?.emails?.length > 0 && (
        <Card className="shadow-sm border-sky-200" data-testid="lead-emails-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
              <Mail className="h-4 w-4 text-sky-600" /> Scambio email ({emailLog.count})
            </CardTitle>
            {emailLog?.addresses?.length > 1 && (
              <p className="mt-1 text-xs text-muted-foreground" data-testid="email-addresses-note">
                Include tutti gli indirizzi del cliente: {emailLog.addresses.join(", ")}
              </p>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="email-thread">
              {emailLog.emails.map((m) => (
                <div key={m.id} className={`rounded-lg border ${m.source === "gestionale" ? "border-violet-200" : "border-border"}`} data-testid="email-row">
                  {m.source === "gestionale" ? (
                    <div className="flex w-full items-start gap-3 p-3 text-left" data-testid="email-row-gestionale">
                      <span className="mt-0.5 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">Proposta</span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{m.subject || "(senza titolo)"}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {fmtDateTime(new Date(m.internal_ms).toISOString())} · via contratti.italiacorsi.it{m.mailbox ? ` · ${m.mailbox}` : ""}
                        </p>
                        {m.stato && <p className="mt-1 text-xs text-muted-foreground">Stato: <span className="font-medium">{m.stato}</span>{m.totale != null ? ` · € ${m.totale}` : ""}</p>}
                      </div>
                    </div>
                  ) : (
                  <>
                  <button
                    className="flex w-full items-start gap-3 p-3 text-left hover:bg-secondary/40"
                    onClick={() => setOpenEmail(openEmail === m.id ? null : m.id)}
                  >
                    <span className={`mt-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.inbound ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>
                      {m.inbound ? "Ricevuta" : "Inviata"}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{m.subject || "(senza oggetto)"}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {fmtDateTime(new Date(m.internal_ms).toISOString())} · da {m.from} · casella {m.mailbox} · Gmail
                      </p>
                      {openEmail !== m.id && <p className="mt-1 truncate text-xs text-muted-foreground">{m.snippet}</p>}
                    </div>
                  </button>
                  {openEmail === m.id && (
                    <div className="border-t border-border bg-secondary/20 p-3">
                      <p className="mb-2 text-xs text-muted-foreground"><b>A:</b> {m.to}</p>
                      {m.body_html ? (
                        <div
                          className="email-body max-h-[600px] overflow-auto rounded-md border border-border bg-white p-4 text-sm text-foreground"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.body_html, { USE_PROFILES: { html: true } }) }}
                        />
                      ) : (
                        <pre className="whitespace-pre-wrap break-words font-sans text-sm text-foreground">{m.body || m.snippet}</pre>
                      )}
                    </div>
                  )}
                  </>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-display text-base tracking-tight">Dati normalizzati</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Fld label="Nome" testid="field-first_name" value={field("first_name")} onChange={(v) => setField("first_name", v)} />
              <Fld label="Cognome" testid="field-last_name" value={field("last_name")} onChange={(v) => setField("last_name", v)} />
              <Fld label="Email" testid="field-email" value={field("email")} onChange={(v) => setField("email", v)} />
              <Fld label="Telefono" testid="field-phone" value={field("phone")} onChange={(v) => setField("phone", v)} />
              <Fld label="Professione" testid="field-profession" value={field("profession")} onChange={(v) => setField("profession", v)} />
              <Fld label="Campagna originale" testid="field-campaign" value={field("campaign")} onChange={(v) => setField("campaign", v)} />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Consenso privacy</Label>
                <div className="mt-1"><ConsentBadge value={lead.normalized?.privacy_consent} /></div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Consenso mailing list</Label>
                <div className="mt-1"><ConsentBadge value={lead.normalized?.mailing_consent} /></div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="font-display text-base tracking-tight">Classificazione</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {lead.pipedrive && (
              <div data-testid="pipedrive-presence">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Stato Pipedrive</Label>
                <div className="mt-1">
                  {lead.pipedrive.presence === "gia_presente" ? (
                    <span className="inline-flex flex-wrap items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
                      Già presente — lead vecchio{lead.pipedrive.presence_owner ? `, assegnato a ${lead.pipedrive.presence_owner}` : ""}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                      Nuovo{lead.pipedrive.matched === false ? " — non presente in Pipedrive" : ""}
                    </span>
                  )}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Stato</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1" data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Corso</Label>
              <Select value={courseId} onValueChange={setCourseId}>
                <SelectTrigger className="mt-1" data-testid="course-select"><SelectValue placeholder="Nessun corso" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Non riconosciuto</SelectItem>
                  {(courses || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.official_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-md bg-secondary/60 p-3 text-xs text-muted-foreground">
              <p>Canale: {lead.channel || "Altro"}</p>
              <p className="mt-1">Ricevuto: {new Date(lead.received_at).toLocaleString("it-IT", { timeZone: "Europe/Rome" })}</p>
              <p className="mt-1 break-all">Gmail ID: {lead.gmail_message_id}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Original data / email */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
            <Mail className="h-4 w-4" /> Email originale
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="original">
            <TabsList>
              <TabsTrigger value="original" data-testid="tab-original">Dati estratti</TabsTrigger>
              <TabsTrigger value="body" data-testid="tab-body">Testo email</TabsTrigger>
            </TabsList>
            <TabsContent value="original" className="mt-4">
              <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                {Object.entries(lead.original || {}).map(([k, v]) => (
                  <div key={k} className="flex gap-2 border-b border-border py-1.5">
                    <span className="w-40 flex-shrink-0 text-muted-foreground">{k}</span>
                    <span className="break-words">{String(v ?? "—")}</span>
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="body" className="mt-4">
              <div className="mb-3 space-y-1 text-sm">
                <p><span className="text-muted-foreground">Oggetto:</span> {lead.raw?.subject}</p>
                <p><span className="text-muted-foreground">Da:</span> {lead.raw?.from}</p>
              </div>
              <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md bg-slate-900 p-4 text-xs leading-relaxed text-slate-100" data-testid="email-body">
                {lead.raw?.body_text || "(nessun testo disponibile)"}
              </pre>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

function Fld({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      <Input className="mt-1" data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function ContactTimeline({ received, history, calls, emails }) {
  const recv = received ? new Date(received).getTime() : null;
  const events = [];
  (history || []).forEach((h) => events.push({ t: new Date(h.received_at).getTime(), kind: "request", h }));
  (calls || []).forEach((c) => events.push({ t: new Date(c.start_time).getTime(), kind: "call", c }));
  (emails || []).forEach((m) => events.push({ t: m.internal_ms, kind: "email", m }));
  events.sort((a, b) => a.t - b.t);
  if (events.length === 0) return null;

  const fmtDT = (t) => new Date(t).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
  const fmtGap = (h) => {
    if (h == null) return "";
    const a = Math.abs(h);
    if (a < 1) return `${Math.round(a * 60)} min`;
    if (a < 48) return `${a.toFixed(1)} h`;
    return `${(a / 24).toFixed(1)} g`;
  };
  const fmtDur = (s) => { s = Number(s) || 0; const m = Math.floor(s / 60); return m > 0 ? `${m}m ${s % 60}s` : `${s}s`; };

  const firstCall = (calls || []).filter((c) => !c.inbound && recv != null && new Date(c.start_time).getTime() >= recv)
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))[0];
  const firstEmail = (emails || []).filter((m) => !m.inbound && recv != null && m.internal_ms >= recv)
    .sort((a, b) => a.internal_ms - b.internal_ms)[0];
  const callH = firstCall ? (new Date(firstCall.start_time).getTime() - recv) / 3600000 : null;
  const emailH = firstEmail ? (firstEmail.internal_ms - recv) / 3600000 : null;
  const firstH = [callH, emailH].filter((x) => x != null).sort((a, b) => a - b)[0] ?? null;

  const META = {
    request: { icon: History, dot: "bg-slate-400", label: "Richiesta lead" },
    call: { icon: Phone, dot: "bg-emerald-500", label: "Chiamata" },
    email: { icon: Mail, dot: "bg-sky-500", label: "Email" },
  };

  return (
    <Card className="shadow-sm" data-testid="contact-timeline-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
          <Clock className="h-4 w-4" /> Timeline del contatto
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="responsiveness-banner">
          <div className={`rounded-lg border p-3 ${firstH == null ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50"}`}>
            <p className="flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground"><Zap className="h-3.5 w-3.5" /> Primo contatto</p>
            <p className="mt-1 text-sm font-semibold">{firstH == null ? "Mai contattato dopo la richiesta" : `dopo ${fmtGap(firstH)}`}</p>
          </div>
          <div className="rounded-lg border border-border p-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Prima chiamata</p>
            <p className="mt-1 text-sm font-semibold">{callH == null ? "—" : `dopo ${fmtGap(callH)}`}</p>
          </div>
          <div className="rounded-lg border border-border p-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Prima email inviata</p>
            <p className="mt-1 text-sm font-semibold">{emailH == null ? "—" : `dopo ${fmtGap(emailH)}`}</p>
          </div>
        </div>

        <ol className="relative border-l-2 border-border pl-5">
          {events.map((e, i) => {
            const m = META[e.kind];
            const Icon = m.icon;
            return (
              <li key={i} className="mb-4 last:mb-0" data-testid="timeline-event">
                <span className={`absolute -left-[7px] mt-1 h-3 w-3 rounded-full ${m.dot}`} />
                <div className="flex flex-wrap items-center gap-2">
                  <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm font-semibold">{fmtDT(e.t)}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{m.label}</span>
                  {e.kind === "request" && e.h.is_current && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">corrente</span>}
                  {e.kind === "call" && <span className="text-xs text-muted-foreground">{e.c.inbound ? "in entrata" : "in uscita"} · {e.c.answered ? `risposto ${fmtDur(e.c.duration)}` : "nessuna risposta"}{e.c.agent ? ` · ${e.c.agent}` : ""}</span>}
                  {e.kind === "email" && <span className="text-xs text-muted-foreground">{e.m.inbound ? "ricevuta" : "inviata"} · {e.m.source === "gestionale" ? "contratti.italiacorsi.it" : "Gmail"}{e.m.mailbox ? ` · ${e.m.mailbox}` : ""}</span>}
                </div>
                {e.kind === "request" && (
                  <p className="mt-0.5 text-xs text-muted-foreground">{e.h.channel || "Altro"}{e.h.course_name ? ` · ${e.h.course_name}` : ""}</p>
                )}
                {e.kind === "email" && (
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">{e.m.subject || "(senza oggetto)"}{e.m.source === "gestionale" && e.m.stato ? ` · ${e.m.stato}` : ""}</p>
                )}
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

function Stat2({ label, value }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm font-semibold">{String(value ?? "—")}</p>
    </div>
  );
}

const SUMMARY_TONES = {
  slate: "bg-slate-100 text-slate-600",
  emerald: "bg-emerald-100 text-emerald-700",
  amber: "bg-amber-100 text-amber-700",
  sky: "bg-sky-100 text-sky-700",
};

function SummaryStat({ icon: Icon, label, value, tone, sub }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border p-3">
      <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${SUMMARY_TONES[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="mt-0.5 truncate text-sm font-semibold">{value}</p>
        {sub && <p className="truncate text-xs text-muted-foreground" title={sub}>{sub}</p>}
      </div>
    </div>
  );
}
